import { useCallback, useMemo } from 'react';
import { AnyAction } from 'redux';
import { GeneralCatalogItem } from 'data-layer-cr/dist/models';
import { DisplayItem } from 'design-system';
import { useNetworkObserver } from 'data-layer-cr/dist/network';
// functions
import {
  mapCatatalogToDisplay as mapToDisplay,
  findIdByCode,
} from 'src/functions/mapCatatalogToDisplay';
// store
import {
  BENEFICIARY_STATES_REQUEST,
  BENEFICIARY_CITIES_REQUEST,
  BENEFICIARY_TOWNSHIPS_REQUEST,
  BENEFICIARY_COUNTRIES_REQUEST,
  BENEFICIARY_NATIONALITY_REQUEST,
  BENEFICIARY_BIRTH_PLACE_REQUEST,
} from 'src/store/legalEntityCatalog/actions/legalEntityRequestIdentifiers';

type Props = {
  beneficiaryCountries: GeneralCatalogItem[];
  beneficiaryCities: GeneralCatalogItem[];
  beneficiaryCountryStates: GeneralCatalogItem[];
  beneficiaryTownships: GeneralCatalogItem[];
  beneficiaryNationality: GeneralCatalogItem[];
  beneficiaryBirthPlace: GeneralCatalogItem[];
  getBeneficiaryCitiesAction: (value: string | number) => Promise<AnyAction>;
  getBeneficiaryTownshipsAction: (value: string | number) => Promise<AnyAction>;
};

type HookResult = {
  requestingBeneficiaryCountries: boolean;
  requestingBeneficiaryStates: boolean;
  requestingBeneficiaryCities: boolean;
  requestingBeneficiaryTownships: boolean;
  requestingBeneficiaryNationality: boolean;
  requestingBeneficiaryBirthPlace: boolean;
  beneficiaryCountryOptions: DisplayItem[];
  beneficiaryStateOptions: DisplayItem[];
  beneficiaryCityOptions: DisplayItem[];
  beneficiaryTownshipOptions: DisplayItem[];
  beneficiaryNationalityOptions: DisplayItem[];
  beneficiaryBirthPlaceOptions: DisplayItem[];
  fetchBeneficiaryCities: (value: string) => void;
  fetchBeneficiaryTownships: (value: string) => void;
};

/**
 * Returns external parameters provided by the old link
 */
export default function useBeneficiaryAddressInformation({
  beneficiaryCountries,
  beneficiaryCities,
  beneficiaryCountryStates,
  beneficiaryTownships,
  beneficiaryNationality,
  beneficiaryBirthPlace,
  getBeneficiaryCitiesAction,
  getBeneficiaryTownshipsAction,
}: Props): HookResult {
  const beneficiaryCountriesRequestStatus = useNetworkObserver(
    BENEFICIARY_COUNTRIES_REQUEST,
  );
  const beneficiaryStatesRequestStatus = useNetworkObserver(
    BENEFICIARY_STATES_REQUEST,
  );
  const beneficiaryCitiesRequestStatus = useNetworkObserver(
    BENEFICIARY_CITIES_REQUEST,
  );
  const beneficiaryTownshipsRequestStatus = useNetworkObserver(
    BENEFICIARY_TOWNSHIPS_REQUEST,
  );
  const beneficiaryNationalityRequestStatus = useNetworkObserver(
    BENEFICIARY_NATIONALITY_REQUEST,
  );
  const beneficiaryBirthPlaceRequestStatus = useNetworkObserver(
    BENEFICIARY_BIRTH_PLACE_REQUEST,
  );

  const beneficiaryCountryOptions = useMemo(
    () => mapToDisplay(beneficiaryCountries),
    [beneficiaryCountries],
  );
  const beneficiaryNationalityOptions = useMemo(
    () => mapToDisplay(beneficiaryNationality),
    [beneficiaryNationality],
  );
  const beneficiaryBirthPlaceOptions = useMemo(
    () => mapToDisplay(beneficiaryBirthPlace),
    [beneficiaryBirthPlace],
  );

  const beneficiaryStateOptions = useMemo(
    () => mapToDisplay(beneficiaryCountryStates),
    [beneficiaryCountryStates],
  );
  const beneficiaryCityOptions = useMemo(
    () => mapToDisplay(beneficiaryCities),
    [beneficiaryCities],
  );
  const beneficiaryTownshipOptions = useMemo(
    () => mapToDisplay(beneficiaryTownships),
    [beneficiaryTownships],
  );

  const fetchBeneficiaryCities = useCallback(
    (newState: string): void => {
      getBeneficiaryCitiesAction(
        findIdByCode(beneficiaryCountryStates, newState),
      );
    },
    [beneficiaryCountryStates, getBeneficiaryCitiesAction],
  );

  const fetchBeneficiaryTownships = useCallback(
    (newCity: string): void => {
      getBeneficiaryTownshipsAction(findIdByCode(beneficiaryCities, newCity));
    },
    [beneficiaryCities, getBeneficiaryTownshipsAction],
  );

  return {
    requestingBeneficiaryCountries:
      beneficiaryCountriesRequestStatus === 'requesting',
    requestingBeneficiaryStates:
      beneficiaryStatesRequestStatus === 'requesting',
    requestingBeneficiaryCities:
      beneficiaryCitiesRequestStatus === 'requesting',
    requestingBeneficiaryTownships:
      beneficiaryTownshipsRequestStatus === 'requesting',
    requestingBeneficiaryNationality:
      beneficiaryNationalityRequestStatus === 'requesting',
    requestingBeneficiaryBirthPlace:
      beneficiaryBirthPlaceRequestStatus === 'requesting',
    beneficiaryCountryOptions,
    beneficiaryStateOptions,
    beneficiaryCityOptions,
    beneficiaryTownshipOptions,
    beneficiaryNationalityOptions,
    beneficiaryBirthPlaceOptions,
    fetchBeneficiaryCities,
    fetchBeneficiaryTownships,
  };
}
